import { useCallback, useState } from "react";
import { getFamilyConfigForHost } from "../lib/families";
import { isIOS, promptInstall, useInstallState } from "../lib/installPrompt";
import { NotificationToggle } from "./NotificationToggle";

/**
 * Home-page nudge, one card at a time:
 *   1. Not installed → "Add to your home screen": a one-tap Install button
 *      where the browser offers it (Chrome/Edge), written Share → Add to Home
 *      Screen steps on iPhone/iPad (Safari has no install API).
 *   2. Installed (or a browser that can notify without installing) and
 *      notifications never asked → "Turn on notifications".
 * Hidden once done; "Not now" hides that card on this device for 30 days.
 */

const INSTALL_KEY = "install-hint.dismissed-at";
const NOTIFY_KEY = "notify-hint.dismissed-at";
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

function snoozed(key: string): boolean {
  try {
    const at = Number(localStorage.getItem(key));
    return at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snooze(key: string) {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // Private mode: the card just comes back next visit.
  }
}

function notificationsUnasked(): boolean {
  if (!("Notification" in window)) return false;
  if (Notification.permission !== "default") return false;
  try {
    return !localStorage.getItem("push-token");
  } catch {
    return true;
  }
}

export function InstallAppCard() {
  const { installed, canPrompt } = useInstallState();
  const [installSnoozed, setInstallSnoozed] = useState(() => snoozed(INSTALL_KEY));
  const [notifySnoozed, setNotifySnoozed] = useState(() => snoozed(NOTIFY_KEY));
  const [notifyDone, setNotifyDone] = useState(false);
  const ios = isIOS();

  // Only family domains ship a manifest + icon; anywhere else iOS would add a
  // plain bookmark that can't notify. Dev hosts are let through for testing.
  const family = getFamilyConfigForHost();
  const installable = family !== null || import.meta.env.DEV;

  const onNotifyChange = useCallback((status: string) => {
    if (status === "enabled" || status === "denied") setNotifyDone(true);
  }, []);

  const showInstall = installable && !installed && !installSnoozed && (canPrompt || ios);
  // On iPhone, Safari only has notifications inside the home-screen app, so
  // the install card covers it there until then.
  const showNotify =
    !showInstall && !notifySnoozed && !notifyDone && notificationsUnasked() && (installed || !ios);

  if (showInstall) {
    const siteName = family ? `the ${family.name} app` : "the app";
    return (
      <div className="card stack" style={{ padding: "var(--s-3)", gap: "var(--s-2)", borderLeft: "4px solid var(--c-accent)" }}>
        <strong>Add {siteName} to your home screen</strong>
        <p className="text-muted" style={{ margin: 0, fontSize: "0.9rem" }}>
          It opens like an app with its own icon, and can notify you when
          something new is posted.
          {ios && " On iPhone, notifications only work from the home-screen app."}
        </p>
        {ios ? (
          <ol style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.9rem", lineHeight: 1.6 }}>
            <li>
              Tap <ShareIcon /> <strong>Share</strong> in Safari (on newer
              iPhones it's under the <strong>•••</strong> button).
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Tap <strong>Add</strong>, then open the site from the new icon.
            </li>
          </ol>
        ) : null}
        <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
          {!ios && (
            <button type="button" onClick={() => void promptInstall()}>
              Install app
            </button>
          )}
          <button
            type="button"
            className={ios ? "btn-secondary" : "btn-tertiary"}
            onClick={() => {
              snooze(INSTALL_KEY);
              setInstallSnoozed(true);
            }}
          >
            {ios ? "Done" : "Not now"}
          </button>
        </div>
      </div>
    );
  }

  if (showNotify) {
    return (
      <div className="card stack" style={{ padding: "var(--s-3)", gap: "var(--s-2)", borderLeft: "4px solid var(--c-accent)" }}>
        <strong>Turn on notifications</strong>
        <p className="text-muted" style={{ margin: 0, fontSize: "0.9rem" }}>
          Get a notification on this device when something new is posted.
          You can turn them off again from your profile.
        </p>
        <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap", alignItems: "center" }}>
          <NotificationToggle onChange={onNotifyChange} />
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => {
              snooze(NOTIFY_KEY);
              setNotifySnoozed(true);
            }}
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/** The iOS share glyph (square with an up arrow), inline with the text. */
function ShareIcon() {
  return (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ verticalAlign: "-2px" }}
    >
      <path d="M7 1v11M3.5 4.5 7 1l3.5 3.5" />
      <path d="M4.5 7.5H2a1 1 0 0 0-1 1V16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8.5a1 1 0 0 0-1-1H9.5" />
    </svg>
  );
}
